import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowUp, FaArrowDown, FaCommentAlt, FaShareAlt, FaPlay, FaLink } from 'react-icons/fa';
import './Master.css'; // Updated import
import usaIcon from '../../Assets/Images/UnitedStatesSVG.svg';
import { getStateIcon } from '../../utils/stateIcons';
import axiosInstance from '../../axiosConfig';
import SharingComponent from "./SharingComponent";

const BASE_S3_URL = "https://messageboard-images.s3.amazonaws.com";

const fetchUsername = async (userId, setUsernameCallback) => {
  try {
    const response = await axiosInstance.get(`/messageboard/usernames/${userId}/`);
    setUsernameCallback(response.data.username);
  } catch (error) {
    console.error('Error fetching username:', error);
    setUsernameCallback('Unknown User');
  }
};

const fetchReplyCount = async (postId, setReplyCount) => {
  try {
    const response = await axiosInstance.get(`/messageboard/forums/reply-count/${postId}/`);
    setReplyCount(response.data.reply_count);
  } catch (error) {
    console.error('Error fetching reply count:', error);
    setReplyCount(0);
  }
};

// Video Player Component
const VideoPlayer = ({ video }) => {
  const [isPlaying, setIsPlaying] = useState(false);

  if (!video) return null;

  return (
    <div className="video-container">
      {!isPlaying ? (
        <div className="video-thumbnail" onClick={() => setIsPlaying(true)}>
          <img
            src={video.thumbnail_url}
            alt="Video thumbnail"
          />
          <div className="video-play-overlay">
            <FaPlay className="play-icon" />
            <div className="video-duration">
              {Math.round(video.duration)}s
            </div>
          </div>
        </div>
      ) : (
        <video
          controls
          autoPlay
          src={video.video_url}
          poster={video.thumbnail_url}
          style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
        >
          Your browser does not support the video tag.
        </video>
      )}
    </div>
  );
};

// Image Gallery Component
const ImageGallery = ({ images }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  if (!images || images.length === 0) return null;

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="image-gallery">
      {images.length === 1 ? (
        <img
          src={images[0].image_url}
          alt="Post attachment"
          className="single-image"
          onClick={() => openLightbox(0)}
        />
      ) : (
        <div className={`image-grid ${images.length === 2 ? 'two-images' : images.length === 3 ? 'three-images' : 'four-plus-images'}`}>
          {images.slice(0, 4).map((image, index) => (
            <div key={image.id} className="image-container" onClick={() => openLightbox(index)}>
              <img src={image.image_url} alt={`Post attachment ${index + 1}`} />
              {index === 3 && images.length > 4 && (
                <div className="more-images-overlay">
                  +{images.length - 4}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>
              ×
            </button>
            <img src={images[currentImageIndex].image_url} alt="Enlarged view" />
            {images.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={prevImage}>←</button>
                <button className="lightbox-nav next" onClick={nextImage}>→</button>
                <div className="lightbox-counter">
                  {currentImageIndex + 1} / {images.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ForumPost_Display = ({ post }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [upvotes, setUpvotes] = useState(post?.upvotes || 0);
  const [downvotes, setDownvotes] = useState(post?.downvotes || 0);
  const [replyCount, setReplyCount] = useState(post?.replies_count || 0);

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL LOGIC
  useEffect(() => {
    // Guard clause inside useEffect instead of before it
    if (!post?.id) return;

    if (!post.username && post.user) {
      fetchUsername(post.user, setUsername);
    } else {
      setUsername(post.username || 'Unknown User');
    }
    fetchReplyCount(post.id, setReplyCount);
  }, [post?.username, post?.user, post?.id]);

  // Debug logging
  console.log('ForumPost_Display received post:', post);

  // Early return AFTER all hooks
  if (!post || !post.id) {
    console.error('ForumPost_Display: Invalid post data received', post);
    return <div className="error-post">Error: Invalid post data</div>;
  }

  const forumPath = post.state ? `/forums/${post.state}/${post.id}` : `/forums/usa/${post.id}`;
  const iconSrc = post.state ? getStateIcon(post.state) : usaIcon;
  const iconAlt = post.state ? `${post.state} Icon` : 'USA Icon';

  // Safe content handling
  const postContent = post.content || '';
  const contentPreview = postContent && postContent.length > 100
    ? postContent.slice(0, 100) + '...'
    : postContent;

  const handleVote = async (type) => {
    try {
      const response = await axiosInstance.post(`/messageboard/forums/post/${post.id}/vote/`, { type });
      setUpvotes(response.data.upvotes);
      setDownvotes(response.data.downvotes);
    } catch (error) {
      console.error(`Error while voting (${type}):`, error);
    }
  };

  return (
    <div className="feed-post">
      <div className="post-header">
        <div className="forum-details-feed">
          <div className="forum-name">
            <img
              src={iconSrc}
              alt={iconAlt}
              className="forum-icon"
            />
            <strong>{post.state || 'USA Forum'}</strong>
          </div>
        </div>
      </div>

      <Link to={forumPath} className="post-title">
        <h3 className="post-title">{post.title || 'Untitled Post'}</h3>
      </Link>

      <div className="post-meta">
        <Link to={`/memberprofile/${username}`} className="username-link">
          <strong>{username}</strong>
        </Link>
        <span>• {post.created_at ? new Date(post.created_at).toLocaleDateString() : 'Unknown date'}</span>
      </div>

      {/* Content preview - more subtle */}
      {contentPreview && (
        <p className="post-content">{contentPreview}</p>
      )}

      {/* Display media - either video or images */}
      <div className="post-media-container">
        {post.video ? (
          <VideoPlayer video={post.video} />
        ) : post.images && post.images.length > 0 ? (
          <ImageGallery images={post.images} />
        ) : post.image ? (
          // Fallback for legacy single image field
          <img
            src={`${BASE_S3_URL}/${post.image.replace(/^\//, '')}`}
            alt="Post attachment"
            className="single-image"
          />
        ) : null}
      </div>

      {/* Display link if present */}
      {post.link_url && (
        <div className="post-link-url">
          <FaLink className="link-icon" />
          <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="external-link">
            {post.link_url}
          </a>
        </div>
      )}

      <div className="post-footer">
        <div className="voting-section">
          <FaArrowUp className="upvote" onClick={() => handleVote('upvote')} />
          <span>{upvotes}</span>
          <FaArrowDown className="downvote" onClick={() => handleVote('downvote')} />
          <span>{downvotes}</span>
        </div>
        <div className="replies-button">
          <Link to={forumPath} >
            <FaCommentAlt /> {replyCount} {replyCount === 1 ? 'Reply' : 'Replies'}
          </Link>
        </div>
        <div
          className="interaction-buttons"
          onClick={() => setIsModalOpen(true)}
        >
          <FaShareAlt className="share-icon" />
        </div>
      </div>

      {isModalOpen && (
        <SharingComponent
          postUrl={`${window.location.origin}/forums/${post.state || 'USA'}/${post.id}/`}
          postTitle={`Check out this post: ${post.title}`}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default ForumPost_Display;